/**
 * Table events.
 */
import listPostsPost from './products-list';
import { getTableImage } from './product-fetch';
import { savePendingData } from './table-save';
import { clearNotifications, addNotification } from './table-notifications';
import { createItemListModal } from './table-item-list-modal';

const productSelect = async () => {
	const tableTop = document.querySelector('.table-top__main');
	const tableItems = document.querySelectorAll('.table-item');
	const tableTitle = document.querySelector('#design-title');
	const sidebarGrid = document.querySelector('.product-sidebar__grid');
	const sidebarTitle = document.querySelector('.product-sidebar__title');
	const sidebarItems = document.querySelectorAll('.product-sidebar__item');
	const saveFields = document.querySelectorAll('.table-field');
	let productGridEnd = document.querySelector('.product-grid-end');

	document.addEventListener('DOMContentLoaded', () => {
		const pendingExistingId = sessionStorage.getItem('pendingExistingId');

		// New unsaved tabletop: load any pending changes
		if (!dcRestSettings.isTablePost && pendingExistingId === null){
			loadFromSessionStorage();
		}

		// Existing tabletop ID must match the ID saved in pendingExistingId, in order to load pending changes
		if (dcRestSettings.isTablePost){ 
			if (pendingExistingId !== null && dcRestSettings.cpid.toString() === pendingExistingId) {
				loadFromSessionStorage();
			} else {
				// Clear any pending tabletop data stored in session
				sessionStorage.clear();
			}
		}
	});

	/**
	 * Load tabletop from browser's sessionStorage (unsaved changes)
	 */
	const loadFromSessionStorage = () => {
		// Check sessionStorage for an existing tabletop
		const storedItems = JSON.parse(sessionStorage.getItem('pendingItems'));
		const storedTitle = sessionStorage.getItem('pendingTitle');

		// Key does not exist. If changes are an empty tabletop, key will be an empty object.
		if (storedItems === null){
			return;
		}

		// Load any tabletop items that were in sessionStorage
		tableItems.forEach(async (item) => {
			const tableItemImage = item.querySelector('.table-item__image');
			const tableItemInput = item.querySelector('.table-field');

			if (tableItemInput !== null) {
				const catKey = tableItemInput.getAttribute('data-var');

				if (storedItems && storedItems.hasOwnProperty(catKey)) {
					// Add item
					tableItemInput.setAttribute('value', storedItems[catKey]);
					tableItemImage.src = await getTableImage(storedItems[catKey], catKey);
					item.classList.add('table-item--has-image');
				} else {
					// Clear slot
					tableItemInput.setAttribute('value', '');
					tableItemImage.src = `${dcRestSettings.imageDir}placeholder.svg`;
					item.classList.remove('table-item--has-image');
				}
			}

			if (storedTitle && dcRestSettings.isTableTemplate) {
				tableTitle.value = storedTitle;
			}
		});

		// Add notification about unsaved changes
		clearNotifications();
		addNotification('Your design has loaded in unsaved changes. <b>Save</b> your design to make sure your changes are not lost.', ['warning']);
	}

	// TABLETOP ACTIONS.
	// Listener for click event. Individual functions check the event.target to handle different actions.
	document.addEventListener(
		'click',
		(event) => {
			selectTableItem(event);
			selectSidebarItem(event);
			clearAll(event);
			newTabletop(event);
			removeTableItem(event);
			openItemsList(event);
			closeDropdown(event);
		},
		false
	);

	/**
	 * Close actions dropdown
	 * @param   {Event}  event
	 */
	const closeDropdown = (event) => {
		const openDrop = document.querySelector('.pr-drop-open');
		if (openDrop !== null){
			openDrop.classList.remove('pr-drop-open');
		}
	};

	/**
	 * Create and Open Items List modal
	 * @param   {Event}  event  [event description]
	 */
	const openItemsList = (event) => {
		if (!event.target.classList.contains('dc-items-list')) {
			return;
		}
		event.stopPropagation();
		createItemListModal();
	}

	/**
	 * Create new tabletop. Clear any pending changes and redirect to Design Center page.
	 * @param {Event} event 
	 */
	const newTabletop = (event) => {
		if (!event.target.classList.contains('dc-new')) {
			return;
		}
		event.stopPropagation();
		sessionStorage.clear();
		window.location.assign('/design-center/');
	}

	/**
	 * Clear everything. All items from the tabletop; visuals, values, sidebar, etc.
	 */
	const clearAll = async (event) => {
		if (!event.target.classList.contains('dc-clear')) {
			return;
		}
		event.stopPropagation();

		const tableHasImages = document.querySelectorAll('.table-item--has-image');

		if (tableHasImages.length < 1) {
			event.target.blur();
			return;
		}

		// Don't create if modal already open
		if (document.querySelector('.confirm-dialog__bg') !== null){
			return;
		}

		await confirmText('Are you sure you want to clear the design?');

		const confBg = document.querySelector('.confirm-dialog__bg');
		const confDialog = confBg.querySelector('.confirm-dialog');

		confDialog.addEventListener('click', async (event) => {
			// Clear design
			if (event.target.matches('#yes')) {
				confBg.remove();
				await clearTableVisuals();
				await clearTableInputVals();
				await clearActiveSidebar();

				// Unsaved changes. Save pending data in browser sessionStorage.
				savePendingData();

				// Add notification that design was cleared
				clearNotifications();
				addNotification('Your design has been cleared.', ['success']);
			}

			// Cancel
			if (event.target.matches('#no')) {
				confBg.remove();
			}
		});
	};

	/**
	 * Tabletop item category is selected on the tabletop (via plus button).
	 * E.g. glassware, small plate, linen
	 * @param {Event} event 
	 */
	const selectTableItem = async (event) => {
		if (
			event.target.matches('.table-item__btn') ||
			event.target.matches('.table-item__image') ||
			event.target.matches('.table-item:not(.table-item--has-image) .table-item__close')
		) {
			const selectedTableItem = event.target.closest('.table-item');
			await activateItemSelection(selectedTableItem);
		}

		if (
			event.target.matches('.dc-extra__btn')
		) {
			const selectedTableItem = event.target.closest('.dc-extra');
			await activateItemSelection(selectedTableItem);
		}
	};

	/**
	 * Item is selected in the sidebar. Add to tabletop.
	 * @param {Event} event 
	 */
	const selectSidebarItem = async (event) => {
		if (
			event.target.matches('.product-sidebar__item') ||
			event.target.matches('.product-sidebar__image') ||
			event.target.matches('.product-title')
		) {
			// Item that was selected on sidebar
			const selectedSideItem = event.target.closest('.product-sidebar__item');

			// Active item type/slot on tabletop
			const tableTopCurrent = tableTop.querySelector('.table-item.is-active');
			const tableItemSlug = tableTopCurrent.getAttribute('data-var');

			const flatwareSlugs = ['dc_salad_fork','dc_knife','dc_spoon','dc_dinner_fork'];
			if (flatwareSlugs.includes(tableItemSlug)){
				// Flatware-specific
				// If item is flatware, assign the same product to the rest of the flatware slots
				let slots = [
					tableTop.querySelector('.table-item[data-var="dc_salad_fork"]'),
					tableTop.querySelector('.table-item[data-var="dc_knife"]'),
					tableTop.querySelector('.table-item[data-var="dc_spoon"]'),
					tableTop.querySelector('.table-item[data-var="dc_dinner_fork"]')
				];
				// All concurrently
				slots.forEach(async (tableItemElement) => {
					await assignTabletopItem(selectedSideItem, tableItemElement);
				});
				// One at a time, in sequence
				// for (let index = 0; index < slots.length; index++) {
				// 	const tableItemElement = slots[index];
				// 	await assignTabletopItem(selectedSideItem, tableItemElement);
				// }
			} else {
				// Default. 
				// Assign selected item to active slot.
				await assignTabletopItem(selectedSideItem, tableTopCurrent);
			}
		}
	};

	const clearActiveTableItem = async () => {
		tableItems.forEach((item) => {
			item.classList.remove('is-active');
		});
	};


	/**
	 * Clear visuals of all tabletop items; background images, classes. Reset to default with placeholders.
	 */
	const clearTableVisuals = async () => {
		await clearActiveTableItem();
		tableItems.forEach((item) => {
			const tableItemImage = item.querySelector('.table-item__image');
			item.classList.remove('table-item--has-image');

			if (tableItemImage) {
				tableItemImage.src = `${dcRestSettings.imageDir}placeholder.svg`;
			}
		});
		sessionStorage.clear();
	};

	/**
	 * Remove single item from the tabletop (clear item slot. clicking X to remove)
	 * @param {Event} event 
	 */
	const removeTableItem = async (event) => {
		if (
			event.target.matches('.table-item--has-image .table-item__close')
		) {
			// Table item where click to remove event occurred
			const itemToClear = event.target.closest('.table-item');

			if (itemToClear !== null){
				const tableItemSlug = itemToClear.getAttribute('data-var');
				const flatwareSlugs = ['dc_salad_fork','dc_knife','dc_spoon','dc_dinner_fork'];

				if (flatwareSlugs.includes(tableItemSlug)){
					// Flatware-specific
					// If item is flatware, assign the same product to the rest of the flatware slots
					let slots = [
						tableTop.querySelector('.table-item[data-var="dc_salad_fork"]'),
						tableTop.querySelector('.table-item[data-var="dc_knife"]'),
						tableTop.querySelector('.table-item[data-var="dc_spoon"]'),
						tableTop.querySelector('.table-item[data-var="dc_dinner_fork"]')
					];
					slots.forEach((tableItemElement) => {
						clearSingleTableItem(tableItemElement);
					});
				} else {
					// Default
					// Clear single item
					clearSingleTableItem(itemToClear);
				}

				// Unsaved changes. Save pending data in browser sessionStorage.
				savePendingData();
			}
		}
	};

	/**
	 * Clear/remove single table item
	 * @param   {HTMLElement}  itemToClear  .table-item element
	 * @return  {void} 
	 */
	const clearSingleTableItem = function(itemToClear){
		if (itemToClear === null){
			return;
		}
		const imageToClear = itemToClear.querySelector('.table-item__image');
		const inputToClear = itemToClear.querySelector('.table-field');

		itemToClear.classList.remove('table-item--has-image');
		imageToClear.src = `${dcRestSettings.imageDir}placeholder.svg`;
		inputToClear.value = '';
	};

	/**
	 * Clear all tabletop item input values
	 */
	const clearTableInputVals = async () => {
		saveFields.forEach((field) => {
			field.value = '';
		});
	};

	/**
	 * Remove active item class from sidebar items
	 */
	const clearActiveSidebar = async () => {
		sidebarItems.forEach((item) => {
			item.classList.remove('is-active');
		});
	};

	/**
	 * Activate sidebar item selection for the selected item type. 
	 * Updates sidebar heading, loader, and queries new items. 
	 * @param {HTMLElement} selectedTableItem 
	 */
	const activateItemSelection = async (selectedTableItem) => {
		await clearActiveTableItem();

		const itemCat = selectedTableItem.getAttribute('data-cat');
		const existingSidebarCat = sidebarGrid.getAttribute('data-cat');

		// Mark slot in tabletop as currently active (green)
		selectedTableItem.classList.add('is-active');

		// If this is the same category of items, we don't need to re-query the same data
		if ( itemCat === existingSidebarCat && itemCat !== null && itemCat !== ''){
			return;
		}

		// Clear existing items from sidebar grid
		sidebarGrid.innerHTML = '';

		// Change item category title in sidebar, and add data attribute with original value
		sidebarTitle.textContent = capitalize(itemCat, true);
		sidebarGrid.setAttribute('data-cat', itemCat);

		// Loading boxes
		sidebarGrid.append(productGridEnd);
		sidebarGrid.classList.add('products-loading');

		// Query new products list
		await productsList(itemCat, 1);
	};

	/**
	 * Capitalizes first letters of words in string.
	 * @param {string} str String to be modified
	 * @param {boolean=false} lower Whether all other letters should be lowercased
	 * @return {string}
	 * @usage capitalize('fix this string');     // -> 'Fix This String'
	 */
	const capitalize = (str, lower = false) => {
		return (lower ? str.toLowerCase() : str).replace(/(?:^|\s|["'([{])+\S/g, match => match.toUpperCase());
	};

	/**
	 * Sidebar items "infinite" loading;
	 * Load more items in sidebar, when scrolled to bottom
	 */
	const endPostsObserver = new IntersectionObserver(async (entries) => {
		// If intersectionRatio is 0, the target is out of view
		if (entries[0].intersectionRatio <= 0) {
			return;
		}

		let nextPage = productGridEnd.getAttribute('data-page');
		let moreCat = sidebarGrid.getAttribute('data-cat');

		await productsList(moreCat, ++nextPage);
	}, {
		root: sidebarGrid,
		rootMargin: '0px 0px 600px 0px'
	});

	/**
	 * Query products list, by category
	 * @param {string} catSlug 
	 * @param {integer} paged 
	 */
	const productsList = async (catSlug, paged) => {
		const postsResponse = await fetch(`${dcRestSettings.endpoint}party-reflections/v1/dc_cat_products/${catSlug}/${paged}`);

		if (!postsResponse.ok) {
			endPostsObserver.unobserve(productGridEnd);
			sidebarGrid.classList.remove('products-loading');
			console.log(`Products List Status Code: ${postsResponse.status}. If this is 404, that's okay. It just means the last page has been reached.`);
			return;
		}

		const posts = await postsResponse.json();

		for (let post of posts) {
			if (catSlug !== sidebarGrid.getAttribute('data-cat')) {
				return;
			}

			listPostsPost(post);
		}

		// endPostsObserver.disconnect();

		endPostsObserver.unobserve(productGridEnd);

		productGridEnd.setAttribute('data-page', paged);
		endPostsObserver.observe(productGridEnd);
	};

	/**
	 * Add selected sidebar item to the tabletop
	 * @param {HTMLElement} selectedSideItem Item selected in sidebar
	 * @param {HTMLElement} tableItemElement Optional. Which .table-item element to apply selected item to.
	 * @return {Promise}
	 */
	const assignTabletopItem = async (selectedSideItem, tableItemElement) => {
		await clearActiveSidebar();

		selectedSideItem.classList.add('is-active');
		const itemId = selectedSideItem.getAttribute('data-product-id');
		const itemTableAlt = selectedSideItem.querySelector('.product-title').textContent;

		// Change images, set value, and related active classes
		if (tableItemElement !== null){
			const tableItemSlug =	tableItemElement.getAttribute('data-var');
			const tableTopImg = tableItemElement.querySelector('img');
			const currentInput = tableItemElement.querySelector('.table-field');
			const dcImage = await getTableImage(itemId, tableItemSlug);
	
			tableTopImg.setAttribute('src', dcImage);
			tableTopImg.setAttribute('title', itemTableAlt);
			tableItemElement.classList.add('table-item--has-image');
			currentInput.setAttribute('value', itemId);
	
			// Unsaved changes. Save pending data in browser sessionStorage.
			savePendingData();
		}

		return Promise.resolve();
	};

	/**
	 * Open custom clear tabletop dialog
	 * @param {string} messageText 
	 */
	const confirmText = async (messageText) => {
		const footer = document.querySelector('#footer');
		const confBg = document.createElement('div');
		const confDialog = document.createElement('div');
		const confText = document.createElement('p');
		confBg.className = 'confirm-dialog__bg';
		confDialog.className = 'confirm-dialog';
		confText.className = 'has-small-font-size';
		confText.textContent = messageText;

		confBg.appendChild(confDialog);
		confDialog.appendChild(confText);

		confDialog.insertAdjacentHTML('beforeend', '<button class="btn is-style-btn-small-outline" id="yes">Yes, clear.</button><button class="button is-style-btn-small" id="no">No, Keep.</button>');
		footer.after(confBg);

		document.body.classList.add('confirmation-active');
	};
};

export default productSelect;
